import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '~/lib/api';

interface OnboardingStatus {
  has_membership: boolean;
  has_gmail: boolean;
  /** True with Gmail connected OR with TBit-managed sending active. */
  has_email_channel?: boolean;
  send_channel?: 'gmail' | 'domain';
  /** False while no sending domain is live yet — the managed CTA would only fail. */
  domain_channel_available?: boolean;
  sender_address?: string | null;
  has_cv: boolean;
  has_preferences: boolean;
  status: string;
}

const ERRORS: Record<string, string> = {
  missing_gmail_scope: 'No se otorgaron los permisos de Gmail necesarios. Intenta de nuevo y acepta todos los permisos.',
  auth_failed: 'Hubo un error durante la autenticación. Intenta de nuevo.',
  no_code: 'No se recibió el código de autorización. Intenta de nuevo.',
  not_authenticated: 'Debes iniciar sesión primero.',
};

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_SIZE = 5 * 1024 * 1024;

/**
 * An applicant is ready to send either from their own Gmail or from the
 * address TBit manages for them. `has_email_channel` covers both; the
 * `has_gmail` fallback keeps older responses working.
 */
function isConnected(data: OnboardingStatus): boolean {
  return data.has_email_channel ?? data.has_gmail;
}

export default function JobApplyOnboardingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const error = params.get('error');
  const gmailConnected = params.get('gmail') === 'connected';
  const forceStep = params.get('step');

  const fetchStatus = useCallback(() => {
    setLoading(true);
    apiFetch('/job-apply/api/onboarding-status')
      .then((r) => {
        if (r.status === 401) throw new Error('not_authenticated');
        return r.json();
      })
      .then((data: OnboardingStatus) => {
        setStatus(data);
        if (isConnected(data) && data.has_cv && data.status === 'ACTIVE' && !forceStep) {
          navigate('/job-apply/dashboard', { replace: true });
        }
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, gmailConnected]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  if (!status || !isConnected(status)) {
    return (
      <SenderConnectStep
        error={error}
        onConnected={fetchStatus}
        managedAvailable={status?.domain_channel_available ?? true}
      />
    );
  }

  if (!status?.has_cv || forceStep === 'cv') {
    return <CVUploadStep onComplete={() => navigate('/job-apply/dashboard', { replace: true })} />;
  }

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card bg-base-100 shadow-lg max-w-md w-full">
        <div className="card-body items-center text-center">
          <div className="text-success text-5xl mb-2">&#10003;</div>
          <h2 className="card-title text-2xl">¡Todo listo!</h2>
          <p className="text-base-content/70">Tu cuenta está configurada.</p>
          <div className="card-actions mt-4">
            <a href="/job-apply/dashboard" className="btn btn-primary">
              Ir al panel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step: Sender Connect ---------- */

function SenderConnectStep({
  error,
  onConnected,
  managedAvailable,
}: {
  error: string | null;
  onConnected: () => void;
  managedAvailable: boolean;
}) {
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState('');

  const activateManagedSending = useCallback(async () => {
    setActivating(true);
    setActivationError('');
    try {
      const res = await apiFetch('/job-apply/api/onboarding/email-channel', { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setActivationError(body?.error || 'No pudimos activar tu cuenta. Intenta de nuevo.');
        return;
      }
      onConnected();
    } catch {
      setActivationError('Error de conexión. Intenta de nuevo.');
    } finally {
      setActivating(false);
    }
  }, [onConnected]);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card bg-base-100 shadow-lg max-w-md w-full">
        <div className="card-body items-center text-center">
          <img
            src="https://images-bucket-tbitmobile-pdn.s3.amazonaws.com/job-apply/images/tibit-gmail-consent.png"
            alt="TBit te ayuda a postular"
            className="w-32 h-32 object-contain mb-2"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />

          <h2 className="card-title text-2xl">Empieza a postular hoy</h2>
          <p className="text-sm text-base-content/70">
            Nosotros ponemos el correo desde el que salen tus postulaciones y te
            reenviamos cada respuesta a tu correo de siempre.
          </p>

          <div className="text-left w-full space-y-3 my-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <p className="text-sm text-base-content/70">
                Enviamos una postulación personalizada por cada oferta
              </p>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
              </svg>
              <p className="text-sm text-base-content/70">
                Cada respuesta de una empresa llega a tu correo
              </p>
            </div>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-primary mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
              </svg>
              <p className="text-sm text-base-content/70">
                Nunca enviamos spam ni correos no autorizados
              </p>
            </div>
          </div>

          {(error || activationError) && (
            <div className="alert alert-error text-sm w-full">
              {activationError || ERRORS[error!] || `Error: ${error}`}
            </div>
          )}

          <div className="card-actions mt-2 w-full flex-col gap-3">
            {/* Hidden until a sending domain is live: offering it earlier would
                only produce an error the applicant cannot act on. */}
            {managedAvailable && (
              <>
                <button
                  className="btn btn-primary w-full"
                  onClick={activateManagedSending}
                  disabled={activating}
                >
                  {activating && <span className="loading loading-spinner loading-sm" />}
                  Continuar con envío gestionado por TBit
                </button>
                <p className="text-xs text-base-content/50">
                  Sin conectar ninguna cuenta. Tardas menos de un minuto.
                </p>

                <div className="divider text-xs text-base-content/40 my-1">o</div>
              </>
            )}

            <a
              href="/job-apply/auth/start"
              className={`btn w-full gap-2 ${managedAvailable ? 'btn-outline' : 'btn-primary'}`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Prefiero postular desde mi Gmail
            </a>
          </div>

          <details className="collapse collapse-arrow bg-base-200 w-full mt-4">
            <summary className="collapse-title text-sm font-medium">Más información</summary>
            <div className="collapse-content text-xs text-base-content/60 space-y-2">
              <p>
                <strong>Envío gestionado por TBit:</strong> tus postulaciones salen de una
                dirección a tu nombre que administramos nosotros, y todas las respuestas
                se reenvían a tu correo. No pedimos ninguna contraseña.
              </p>
              <p>
                <strong>Desde tu Gmail:</strong> solicitamos permisos de lectura y envío de
                correo para poder (1) enviar postulaciones desde tu cuenta de Gmail y
                (2) detectar cuándo una empresa responde a tu postulación.
                Tus credenciales están encriptadas y nunca se comparten con terceros.
                Puedes revocar el acceso en cualquier momento desde tu cuenta de Google.
              </p>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step: CV Upload ---------- */

function CVUploadStep({ onComplete }: { onComplete: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return 'Solo se aceptan archivos PDF o DOCX.';
    }
    if (f.size > MAX_SIZE) {
      return 'El archivo no puede superar 5 MB.';
    }
    return null;
  }, []);

  const handleFileSelect = useCallback(
    (f: File) => {
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        return;
      }
      setFile(f);
      setError(null);
    },
    [validateFile],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFileSelect(dropped);
    },
    [handleFileSelect],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFileSelect(selected);
    },
    [handleFileSelect],
  );

  const upload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/job-apply/api/cv/upload', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Error ${res.status}`);
      }
      setUploaded(true);
    } catch (err: any) {
      setError(err.message || 'Error al subir el archivo. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }, [file]);

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="card bg-base-100 shadow-lg max-w-md w-full">
        <div className="card-body items-center text-center">
          <div className="flex items-center gap-2 mb-2">
            <span className="badge badge-primary badge-sm">Paso 2</span>
            <span className="text-sm text-base-content/50">de 2</span>
          </div>

          <h2 className="card-title text-2xl">Sube tu hoja de vida</h2>
          <p className="text-base-content/70 text-sm">
            Extraeremos tus habilidades y experiencia para personalizar las
            postulaciones.
          </p>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={onInputChange}
          />

          <div
            role="button"
            tabIndex={0}
            className={`border-2 border-dashed rounded-box p-8 w-full mt-4 transition-colors cursor-pointer ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-base-300 hover:border-primary'
            }`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            {file ? (
              <div className="flex flex-col items-center gap-1">
                <svg
                  className="w-8 h-8 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                  />
                </svg>
                <p className="font-medium text-sm">{file.name}</p>
                <p className="text-xs text-base-content/50">
                  {(file.size / 1024).toFixed(0)} KB
                </p>
              </div>
            ) : (
              <div className="text-base-content/50">
                <svg
                  className="w-8 h-8 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                  />
                </svg>
                <p className="font-medium text-sm">Arrastra tu archivo aqui</p>
                <p className="text-xs mt-1">PDF o DOCX, max 5 MB</p>
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-error text-sm w-full mt-2">
              {error}
            </div>
          )}

          {!uploaded && (
            <div className="card-actions mt-4 w-full">
              <button
                className="btn btn-primary w-full"
                onClick={upload}
                disabled={!file || uploading}
              >
                {uploading && (
                  <span className="loading loading-spinner loading-sm" />
                )}
                Subir hoja de vida
              </button>
            </div>
          )}

          {uploaded && (
            <div className="w-full mt-4 space-y-3">
              <div className="alert alert-success text-sm">
                Hoja de vida subida correctamente.
              </div>
              <button className="btn btn-primary w-full" onClick={onComplete}>
                Continuar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
